import {
  graphQLErrorCode,
  graphQLErrorMessage,
  RATE_LIMITED_CODE,
  rateLimitMessage,
} from '@/utils/apollo-errors';

// Error codes from dispatch-api's auth and mfa modules.
export const MFA_ERROR_CODES = {
  required: 'auth:mfa_required',
  enrollmentRequired: 'auth:mfa_enrollment_required',
  invalidCode: 'auth:mfa_invalid_code',
  challengeInvalid: 'auth:mfa_challenge_invalid',
  stepUpRequired: 'auth:mfa_step_up_required',
  enrollmentInvalid: 'mfa:enrollment-invalid',
  notConfigured: 'mfa:not-configured',
  totpExists: 'mfa:totp-exists',
  passkeyInvalid: 'mfa:passkey-invalid',
  passkeyLimit: 'mfa:passkey-limit',
  noPasskey: 'mfa:no-passkey',
  disabled: 'mfa:disabled',
} as const;

/** A step-up the person closed without verifying. */
export const STEP_UP_CANCELLED_MESSAGE =
  'This needs your second step. Try again and complete the check.';

/**
 * A message for a failed MFA call that tells the person what to do next.
 * `recoveryCode` picks the wording for a wrong recovery code.
 */
export function mfaErrorMessage(
  err: unknown,
  { recoveryCode = false }: { recoveryCode?: boolean } = {},
): string {
  switch (graphQLErrorCode(err)) {
    case MFA_ERROR_CODES.invalidCode:
      return recoveryCode
        ? 'That recovery code didn’t work. Check it and try again. Each code works only once.'
        : 'That code didn’t work. Enter the newest code from your authenticator app.';
    case RATE_LIMITED_CODE:
      return rateLimitMessage(err);
    case MFA_ERROR_CODES.challengeInvalid:
      return 'Your sign-in expired or had too many wrong codes. Enter your password again.';
    case MFA_ERROR_CODES.enrollmentInvalid:
      return 'This setup expired. Start again to get a new QR code.';
    // The step-up prompt (StepUpPrompt) opens first; this shows only when
    // the person closed it.
    case MFA_ERROR_CODES.stepUpRequired:
      return STEP_UP_CANCELLED_MESSAGE;
    case MFA_ERROR_CODES.notConfigured:
      return 'Two-step verification is not available right now. Contact support.';
    case MFA_ERROR_CODES.disabled:
      return 'Two-step verification is turned off for your account.';
    case MFA_ERROR_CODES.passkeyInvalid:
      return (
        graphQLErrorMessage(err) ??
        'The passkey could not be verified. Try again.'
      );
    case MFA_ERROR_CODES.passkeyLimit:
      return 'You have the most passkeys allowed. Remove one to add another.';
    case MFA_ERROR_CODES.totpExists:
      return 'An authenticator app is already set up. Remove it first to add a new one.';
    default:
      return graphQLErrorMessage(err) ?? 'Something went wrong. Try again.';
  }
}
